import { useTranslation } from "react-i18next";
import { useCustomListDetails } from "./useCustomListDetails";
import {
  AppLayout,
  LoadingPage,
  NavigationBlocker,
  OperatorModal,
  PageSidebar,
  SimpleErrorNotification,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { getMultilingualString } from "shared/utils";
import { getTagPropsFromCustomListType } from "shared/utils/getTagPropsFromCustomListType";
import {
  CustomListDetailsTabs,
  getCustomListDetailsTabsConfig,
} from "./components";
import { CustomListDetailsTabEnum } from "./CustomListDetailsTabEnum";
import { useEffect, useRef, useState } from "react";
import { PageActions } from "shared/components/organisms/PageActions";
import { ListItems, ListDetails, ListNotes, ListStats } from "./views";
import { CustomListDetailsContextProvider } from "./CustomListDetailsContext";
import { useListDetailsForm } from "./views/ListDetails/useListDetailsForm";
import { useToastContext } from "@ttc3k/trekker";
import {
  ListByIdDocument,
  ListByIdQuery,
  ListManyDocument,
} from "gql/generated";
import { useAppContext } from "AppContext";
import { BuildNewListModal } from "apps/customLists/components";

export const CustomListDetails = () => {
  const { t } = useTranslation();
  const { setIsNavigationBlocked } = useAppContext();
  const { toastFactory } = useToastContext();

  const {
    list,
    error,
    loading,
    isEditModalOpen,
    operatorModalOperatorId,
    pageActions,
    pageActionToDisable,
    handleActionClick,
    handleEditListItemsClick,
    handleOperatorDetailsClick,
    setFilteredOperatorEntityIds,
    setOperatorModalOperatorId,
  } = useCustomListDetails();

  /**
   * Setup the tabs
   */
  const customListDetailsTabsConfig = getCustomListDetailsTabsConfig(t);
  const [tab, setTab] = useState<CustomListDetailsTabEnum>(
    customListDetailsTabsConfig[0].value as CustomListDetailsTabEnum,
  );

  /**
   * Setup the list details form
   */
  const formId = "list-details-form";
  const { form, onSubmit } = useListDetailsForm({
    list: list as ListByIdQuery["listById"],
    onSuccess: () => {
      form.reset();
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t("customLists:LIST_DETAILS.FORM.SUCCESS", {
          listName: getMultilingualString(list?.name ?? {}),
        }),
      });
    },
    onError: () => {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("customLists:LIST_DETAILS.FORM.ERROR", {
          listName: getMultilingualString(list?.name ?? {}),
        }),
      });
    },
  });

  useEffect(() => {
    setIsNavigationBlocked(form.formState.isDirty);
  }, [form.formState.isDirty, setIsNavigationBlocked]);

  // We need a ref of the scrollable element to pass to the virtualizer
  const virtualScrollRef = useRef<HTMLDivElement>(null);

  if (loading) return <LoadingPage />;

  if (error) throw new Error(error.message);

  if (!list) return <SimpleErrorNotification />;

  /**
   * Handle tab changes and displaying the correct tab content
   */
  const handleTabChange = (tab: CustomListDetailsTabEnum) => {
    setTab(tab);
  };

  const getCurrentListDetailsView = (tab: CustomListDetailsTabEnum) => {
    switch (tab) {
      case CustomListDetailsTabEnum.ListItems:
        return (
          <ListItems
            virtualScrollRef={virtualScrollRef}
            onEditItemsClick={handleEditListItemsClick}
            onOperatorClick={handleOperatorDetailsClick}
            onOperatorEntityIdsChange={setFilteredOperatorEntityIds}
          />
        );
      case CustomListDetailsTabEnum.ListDetails:
        return <ListDetails form={form} formId={formId} onSubmit={onSubmit} />;
      case CustomListDetailsTabEnum.ListNotes:
        return <ListNotes />;
      case CustomListDetailsTabEnum.ListStats:
        return <ListStats />;
    }
  };

  return (
    <CustomListDetailsContextProvider list={list}>
      <AppLayout
        ref={virtualScrollRef}
        sidebar={
          <PageSidebar
            title={getMultilingualString(list.name)}
            tagProps={getTagPropsFromCustomListType(list.type)}
            buttonGroupProps={{
              py: "150",
            }}
            primaryButtonProps={
              tab === CustomListDetailsTabEnum.ListDetails && {
                /* Save changes button */ visual: "primary",
                children: t("customLists:LIST_DETAILS.CTA_BUTTONS.SAVE_COPY"),
                isLoading: form.formState.isSubmitting,
                disabled: !form.formState.isDirty,
                form: formId,
                type: "submit",
              }
            }
            secondaryButtonProps={{
              visual: "primary",
              mode: "light",
              children: t("customLists:LIST_DETAILS.CTA_BUTTONS.EDIT_COPY"),
              onClick: handleEditListItemsClick,
            }}
          >
            <PageActions
              title={t("core:ACTIONS")}
              actions={pageActions}
              onActionClick={handleActionClick}
              keyToDisable={pageActionToDisable}
            />
          </PageSidebar>
        }
        content={
          <Flex direction={"column"} h={"fit-content"} w={"full"} gap={"300"}>
            <NavigationBlocker>
              <CustomListDetailsTabs
                currentTab={tab}
                onTabChange={handleTabChange}
              />
            </NavigationBlocker>

            {getCurrentListDetailsView(tab)}
          </Flex>
        }
      />
      <BuildNewListModal
        isOpen={isEditModalOpen}
        onClose={handleEditListItemsClick}
        name={getMultilingualString(list.name)}
        type={list.type}
        defaultList={list}
        updateMutationRefetchQueries={[ListManyDocument, ListByIdDocument]}
      />
      <OperatorModal
        isOpen={operatorModalOperatorId ? true : false}
        operatorId={operatorModalOperatorId}
        onClose={() => setOperatorModalOperatorId("")}
      />
    </CustomListDetailsContextProvider>
  );
};
